import { findTemplateMeta, templateMetaByType } from '@inbx/shared/schemas';
import { useState } from 'react';


import { apiClient } from '../api/client.js';
import {
  useCreateWidget,
  useDeleteWidget,
  useUpdateWidget,
  useWidgets,
  type WidgetRow,
} from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import { StoreSelect } from '../components/store-select.js';
import {
  Button,
  ConfirmModal,
  DrawerPanel,
  EmptyState,
  Field,
  FilterChip,
  IconButton,
  Input,
  PageHeader,
  Select,
} from '../components/ui/index.js';
import { makeChatBody } from '../widgets/chat-body.js';
import { type WidgetDraft, type WidgetType } from '../widgets/editor-shell.js';
import { makeFormBody } from '../widgets/form-body.js';
import { makePromoBody } from '../widgets/promo-body.js';
import { EditorShell as WidgetEditorShellV2 } from '../widgets/shell.js';
import { makeSurveyBody } from '../widgets/survey-body.js';

import '../widgets/shell.css';

export function WidgetsPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const widgetsQ = useWidgets(slug);
  const createWidget = useCreateWidget(slug);
  const deleteWidget = useDeleteWidget(slug);

  const data = (widgetsQ.data ?? []) as WidgetRow[];
  const [filter, setFilter] = useState<'all' | 'form' | 'chat' | 'survey'>('all');
  const [editId, setEditId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // F1: Create form state
  const [newType, setNewType] = useState<'form' | 'chat' | 'survey' | 'promo'>('form');
  const [newName, setNewName] = useState('');
  const [newTemplateId, setNewTemplateId] = useState('');

  const filtered = filter === 'all' ? data : data.filter((w) => w.type === filter);
  const editWidget = editId ? data.find((w) => w.id === editId) : null;

  // Шаблоны (дизайны) — из единого реестра меты @inbx/shared.
  const relevantTemplates = templateMetaByType(newType);

  function handleCreate(): void {
    if (!newName.trim()) return;
    const tmpl = newTemplateId ? findTemplateMeta(newTemplateId) : undefined;
    const body: Record<string, unknown> = { name: newName, type: newType };
    if (tmpl) {
      // Шаблон = дизайн + стартовый контент/поведение. Косметика — переменные поверх.
      body.theme_config = { template_id: tmpl.id };
      body.content_config = { ...tmpl.defaultContent };
      body.behavior_config = { ...(tmpl.defaultBehavior ?? {}) };
      body.media_config = {};
    }
    createWidget.mutate(body as { name: string; type: string }, {
      onSuccess: (w) => {
        setCreateOpen(false);
        setEditId(w.id);
        setNewName('');
        setNewType('form');
        setNewTemplateId('');
      },
    });
  }

  return (
    <div className="inbx-page">
      <PageHeader
        title="Виджеты"
        badge={{ count: data.length }}
        actions={
          <Button variant="primary" icon="ti-plus" onClick={() => setCreateOpen(true)}>
            Создать виджет
          </Button>
        }
        filters={
          <>
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
              Все
            </FilterChip>
            <FilterChip active={filter === 'form'} onClick={() => setFilter('form')}>
              Формы
            </FilterChip>
            <FilterChip active={filter === 'chat'} onClick={() => setFilter('chat')}>
              Чаты
            </FilterChip>
            <FilterChip active={filter === 'survey'} onClick={() => setFilter('survey')}>
              Опросы
            </FilterChip>
          </>
        }
      />
      <div className="inbx-page__body">
        {widgetsQ.isLoading ? null : filtered.length === 0 ? (
          <EmptyState
            icon="ti-forms"
            title="Виджетов нет"
            description="Создай первый виджет — форму, чат или опрос"
            action={{ label: 'Создать первый', onClick: () => setCreateOpen(true) }}
          />
        ) : (
          <div className="inbx-grid-3">
            {filtered.map((w) => (
              <WidgetCard
                key={w.id}
                widget={w}
                onEdit={() => setEditId(w.id)}
                onDuplicate={() => {
                  const ww = w as WidgetRow & {
                    themeConfig?: Record<string, unknown>;
                    contentConfig?: Record<string, unknown>;
                    behaviorConfig?: Record<string, unknown>;
                    mediaConfig?: Record<string, unknown>;
                  };
                  createWidget.mutate({
                    name: `${w.name} (копия)`,
                    type: w.type,
                    theme_config: ww.themeConfig,
                    content_config: ww.contentConfig,
                    behavior_config: ww.behaviorConfig,
                    media_config: ww.mediaConfig,
                  } as { name: string; type: string });
                }}
                onDelete={() => setDeleteId(w.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* F1: Create drawer with real POST */}
      <DrawerPanel
        open={createOpen}
        title="Новый виджет"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!newName.trim() || createWidget.isPending}
            >
              Создать
            </Button>
          </>
        }
      >
        <Field label="Тип" required>
          <Select
            value={newType}
            onChange={(e) => {
              setNewType(e.target.value as 'form' | 'chat' | 'survey' | 'promo');
              setNewTemplateId('');
            }}
          >
            <option value="form">Форма</option>
            <option value="chat">Чат-виджет</option>
            <option value="survey">Опрос</option>
            <option value="promo">Промо</option>
          </Select>
        </Field>
        <Field label="Название" required>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Форма обратной связи"
            autoFocus
          />
        </Field>
        {/* F5: templates for selected type */}
        <Field label="Из шаблона">
          <Select value={newTemplateId} onChange={(e) => setNewTemplateId(e.target.value)}>
            <option value="">— чистый виджет —</option>
            {relevantTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
      </DrawerPanel>

      {/* F4: Delete confirm */}
      <ConfirmModal
        open={!!deleteId}
        title="Удалить виджет?"
        description="Виджет будет удалён. Он перестанет работать на всех сайтах где установлен."
        confirmLabel="Удалить"
        onConfirm={() => {
          if (deleteId) deleteWidget.mutate(deleteId);
          setDeleteId(null);
        }}
        onCancel={() => setDeleteId(null)}
      />

      {editWidget && (
        <WidgetEditor widget={editWidget} workspaceSlug={slug} onClose={() => setEditId(null)} />
      )}
    </div>
  );
}

function WidgetCard({
  widget,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  widget: WidgetRow;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}): JSX.Element {
  const CHAT_META = { label: 'Чат', icon: 'ti-message-circle-2', color: '#2563eb' };
  const TYPE_META: Record<string, { label: string; icon: string; color: string }> = {
    chat: CHAT_META,
    form: { label: 'Форма', icon: 'ti-clipboard-text', color: '#16a34a' },
    survey: { label: 'Опрос', icon: 'ti-chart-bar', color: '#9333ea' },
    promo: { label: 'Промо', icon: 'ti-speakerphone', color: '#ea580c' },
  };
  const meta = TYPE_META[widget.type] ?? CHAT_META;
  return (
    <div className="inbx-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          height: 120,
          background: `linear-gradient(135deg, ${meta.color}22, ${meta.color}0a)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <i className={`ti ${meta.icon}`} style={{ fontSize: 44, color: meta.color }} />
        <span
          className="inbx-pill-badge"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: meta.color,
            color: '#fff',
          }}
        >
          {meta.label}
        </span>
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>{widget.name}</div>
        <div
          style={{
            fontSize: 'var(--inbx-fz-sm)',
            color: 'var(--inbx-text-tertiary)',
            marginBottom: 8,
          }}
        >
          {widget.isActive ? 'Активен' : 'Не активен'}
        </div>
        <WidgetStore widget={widget} />
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="primary" icon="ti-edit" onClick={onEdit}>
            Редактировать
          </Button>
          <IconButton icon="ti-copy" tooltip="Дублировать" onClick={onDuplicate} />
          <IconButton icon="ti-trash" tooltip="Удалить" onClick={onDelete} />
        </div>
      </div>
    </div>
  );
}

/** Магазин виджета: обращения с него попадут во вкладку этого магазина. */
function WidgetStore({ widget }: { widget: WidgetRow }): JSX.Element {
  const slug = useAuthStore((s) => s.user?.workspaceSlug);
  const update = useUpdateWidget(slug);
  return (
    <StoreSelect
      value={widget.storeId ?? null}
      locked={Boolean(
        (widget as { behaviorConfig?: { partner_external_ref?: string } }).behaviorConfig
          ?.partner_external_ref,
      )}
      hint="Обращения с этого виджета попадут во вкладку магазина, заказы и товары — из него."
      onChange={(storeId) => update.mutate({ id: widget.id, patch: { store_id: storeId } })}
    />
  );
}

// ── Диспетчер ────────────────────────────────────────────────────────
function WidgetEditor({
  widget,
  workspaceSlug,
  onClose,
}: {
  widget: WidgetRow;
  workspaceSlug: string | undefined;
  onClose: () => void;
}): JSX.Element {
  return <NewWidgetEditor widget={widget} workspaceSlug={workspaceSlug} onClose={onClose} />;
}

/**
 * NewWidgetEditor — единый редактор на базе EditorShell + per-type body
 * (chat/form/survey/promo). Старые ChatWidgetEditor/FormWidgetEditor/etc.
 * остались в файле как dead-code до отдельной зачистки — НЕ использовать.
 */
function NewWidgetEditor({
  widget,
  workspaceSlug,
  onClose,
}: {
  widget: WidgetRow;
  workspaceSlug: string | undefined;
  onClose: () => void;
}): JSX.Element {
  const widgetAny = widget as unknown as {
    id: string;
    workspaceId: string;
    publicKey: string;
    name: string;
    type: WidgetType;
    isActive: boolean;
    themeConfig?: Record<string, unknown>;
    contentConfig?: Record<string, unknown>;
    behaviorConfig?: Record<string, unknown>;
    mediaConfig?: Record<string, unknown>;
    customCssUrl?: string | null;
  };

  const body =
    widget.type === 'chat'
      ? makeChatBody({
          widgetId: widgetAny.id,
          widgetPublicKey: widgetAny.publicKey,
          workspaceId: widgetAny.workspaceId,
          workspaceSlug,
        })
      : widget.type === 'form'
        ? makeFormBody({ widgetPublicKey: widgetAny.publicKey, workspaceId: widgetAny.workspaceId })
        : widget.type === 'survey'
          ? makeSurveyBody({ widgetPublicKey: widgetAny.publicKey, workspaceId: widgetAny.workspaceId })
          : makePromoBody({ widgetPublicKey: widgetAny.publicKey, workspaceId: widgetAny.workspaceId });

  const defaults = body.defaultDraft();
  const [draft, setDraft] = useState<WidgetDraft>({
    name: widgetAny.name,
    type: widgetAny.type,
    isActive: widgetAny.isActive,
    themeConfig: (widgetAny.themeConfig as Record<string, unknown> | undefined) ?? defaults.themeConfig,
    contentConfig:
      (widgetAny.contentConfig as Record<string, unknown> | undefined) ?? defaults.contentConfig,
    behaviorConfig:
      (widgetAny.behaviorConfig as Record<string, unknown> | undefined) ?? defaults.behaviorConfig,
    mediaConfig: (widgetAny.mediaConfig as Record<string, unknown> | undefined) ?? defaults.mediaConfig,
    customCssUrl: widgetAny.customCssUrl ?? null,
  });

  const updateMut = useUpdateWidget(workspaceSlug);

  const handleSave = async (closeAfter: boolean): Promise<void> => {
    // Сценарий бота, выбранный в редакторе чата, дублируем в колонку
    // bot_scenario_id (рантайм/бандл читают её для запуска бота).
    const scenarioId = (draft.behaviorConfig as { scenario_id?: string | null } | undefined)
      ?.scenario_id;
    await updateMut.mutateAsync({
      id: widgetAny.id,
      patch: {
        name: draft.name,
        is_active: draft.isActive,
        theme_config: draft.themeConfig,
        content_config: draft.contentConfig,
        behavior_config: draft.behaviorConfig,
        media_config: draft.mediaConfig,
        custom_css_url: draft.customCssUrl,
        ...(scenarioId ? { bot_scenario_id: scenarioId } : {}),
      },
    });
    if (closeAfter) onClose();
  };

  const handlePreviewPage = async (): Promise<void> => {
    try {
      const r = await apiClient.post<{ url: string }>(
        `/widgets/${widgetAny.id}/preview-draft`,
        {
          type: draft.type,
          themeConfig: draft.themeConfig,
          contentConfig: draft.contentConfig,
          behaviorConfig: draft.behaviorConfig,
          mediaConfig: draft.mediaConfig,
          customCssUrl: draft.customCssUrl,
        },
        { workspaceSlug },
      );
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.warn('[preview]', e);
    }
  };

  return (
    <WidgetEditorShellV2
      draft={draft}
      body={body}
      onChange={setDraft}
      onSave={handleSave}
      onCancel={onClose}
      onOpenPreviewPage={handlePreviewPage}
    />
  );
}
